import React, { useState, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { Slider } from 'primereact/slider';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'src/redesign/components/Button';
import { PlanType, TicketPack } from 'src/types/types';
import {
  FEATURE_TO_PLAN_MAP,
  PLAN_TIER_MAP,
  TIER_TO_BILLING_TYPE,
  FeatureKey,
} from './featureConfig';
import styles from './PlanRecommendationModal.module.scss';
import { API_URL } from 'src/utils/transport';

interface PlanRecommendationModalProps {
  visible: boolean;
  onHide: () => void;
  plans: PlanType[];
  ticketPacks: TicketPack[];
  onSelectPlan?: (planBillingType: string) => void;
  subscription?: any;
  siteData?: any;
  isYearly?: boolean;
  couponCode?: string;
}

export const PlanRecommendationModal: React.FC<PlanRecommendationModalProps> = ({
  visible,
  onHide,
  plans,
  ticketPacks,
  onSelectPlan,
  subscription,
  siteData,
  isYearly = false,
  couponCode,
}) => {
  // State for user inputs
  const [avgTicketPrice, setAvgTicketPrice] = useState(50);
  const [expectedAttendees, setExpectedAttendees] = useState(500);
  const [recurringEvents, setRecurringEvents] = useState(false);
  const [exceedsQuota, setExceedsQuota] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<Set<FeatureKey>>(new Set());

  // Calculate estimated revenue
  const estimatedRevenue = avgTicketPrice * expectedAttendees;

  // Toggle feature selection
  const toggleFeature = (featureKey: FeatureKey) => {
    const newFeatures = new Set(selectedFeatures);
    if (newFeatures.has(featureKey)) {
      newFeatures.delete(featureKey);
    } else {
      newFeatures.add(featureKey);
    }
    setSelectedFeatures(newFeatures);
  };

  // Recommendation logic (real-time)
  const recommendation = useMemo(() => {
    // STEP 1: Determine minimum required plan tier based on selected features
    let requiredPlanTier = 0;
    const requiredFeatures: string[] = [];
    const reasonBullets: string[] = [];

    // If average ticket price > 0, minimum tier is Professional (paid ticketing requires it)
    if (avgTicketPrice > 0) {
      requiredPlanTier = 1;
      if (!selectedFeatures.has('paid_events')) {
        requiredFeatures.push('Paid ticketing');
      }
    }

    selectedFeatures.forEach((featureKey) => {
      const featureConfig = FEATURE_TO_PLAN_MAP[featureKey];
      if (featureConfig.planTier > requiredPlanTier) {
        requiredPlanTier = featureConfig.planTier;
      }
      requiredFeatures.push(featureConfig.displayName);
    });

    // Build "recommended because" reasons
    if (expectedAttendees > 0) {
      reasonBullets.push(`~${expectedAttendees.toLocaleString()} attendees`);
    }
    if (selectedFeatures.size > 0) {
      // Show top 2 most important features (cap at 2 to avoid noise)
      const topFeatures = Array.from(selectedFeatures)
        .slice(0, 2)
        .map((key) => FEATURE_TO_PLAN_MAP[key].displayName.toLowerCase());
      reasonBullets.push(...topFeatures);
    }

    // STEP 2: Find subscription plan that matches feature requirements AND capacity needs
    const billingType = TIER_TO_BILLING_TYPE[requiredPlanTier as keyof typeof TIER_TO_BILLING_TYPE];

    // Enterprise is custom pricing - handle separately
    if (billingType === 'enterprise') {
      const enterprisePlan = plans.find((p) => p.billing_type === 'enterprise');

      // Calculate Eventbrite cost comparison
      const eventbritePercentageFee = 0.039;
      const eventbriteFixedFee = 1.59;
      const eventbriteCost =
        estimatedRevenue * eventbritePercentageFee + expectedAttendees * eventbriteFixedFee;

      return {
        plan: enterprisePlan || null,
        ticketPack: null,
        allMatchingPacks: [],
        requiredFeatures,
        requiredTier: requiredPlanTier,
        packRequired: false,
        isEnterprise: true,
        estimatedRevenue,
        eventbriteCost,
        reasonBullets,
      };
    }

    let recommendedPlan = plans.find((p) => {
      if (!p.billing_type) return false;

      return (
        p.billing_type === billingType &&
        p.monthly_attendee_quota >= expectedAttendees &&
        p.max_ticket_price >= avgTicketPrice
      );
    });

    // FALLBACK 1: If no plan with sufficient capacity, get the base plan for the tier
    if (!recommendedPlan) {
      recommendedPlan = plans.find((p) => {
        if (!p.billing_type) return false;
        return p.billing_type === billingType;
      });
    }

    // FALLBACK 2: If capacity needs force upgrade, find next tier (but not enterprise)
    if (
      recommendedPlan &&
      (recommendedPlan.monthly_attendee_quota < expectedAttendees ||
        recommendedPlan.max_ticket_price < avgTicketPrice)
    ) {
      const upgradedPlan = plans.find((p) => {
        if (!p.billing_type) return false;
        if (p.billing_type === 'enterprise') return false;

        const planTier = PLAN_TIER_MAP[p.billing_type as keyof typeof PLAN_TIER_MAP];
        if (planTier === undefined) return false;

        return (
          planTier >= requiredPlanTier &&
          p.monthly_attendee_quota >= expectedAttendees &&
          p.max_ticket_price >= avgTicketPrice
        );
      });

      if (upgradedPlan) {
        recommendedPlan = upgradedPlan;
      }
    }

    // STEP 3: Find recommended ticket packs
    const matchingPacks = ticketPacks
      .filter(
        (pack) => pack.ticket_quota >= expectedAttendees && pack.max_ticket_price >= avgTicketPrice,
      )
      .sort((a, b) => a.price - b.price);

    let bestPack = null;
    let packRequired = false;

    if (
      recommendedPlan &&
      (recommendedPlan.monthly_attendee_quota < expectedAttendees || exceedsQuota)
    ) {
      packRequired = true;
      bestPack = matchingPacks.length > 0 ? matchingPacks[0] : null;
    } else if (matchingPacks.length > 0) {
      bestPack = matchingPacks[0];
    }

    // Calculate Eventbrite cost comparison
    const eventbritePercentageFee = 0.039;
    const eventbriteFixedFee = 1.59;
    const eventbriteCost =
      estimatedRevenue * eventbritePercentageFee + expectedAttendees * eventbriteFixedFee;

    // Calculate TicketSpot cost
    const ticketSpotMonthlyCost = recommendedPlan ? recommendedPlan.monthly_price : 0;
    const ticketSpotPackCost = packRequired && bestPack ? bestPack.price : 0;
    const ticketSpotTotalCost = ticketSpotMonthlyCost + ticketSpotPackCost;

    return {
      plan: recommendedPlan,
      ticketPack: bestPack,
      allMatchingPacks: matchingPacks.slice(0, 3),
      requiredFeatures,
      requiredTier: requiredPlanTier,
      packRequired,
      isEnterprise: false,
      estimatedRevenue,
      eventbriteCost,
      ticketSpotCost: ticketSpotTotalCost,
      reasonBullets,
    };
  }, [
    selectedFeatures,
    avgTicketPrice,
    expectedAttendees,
    recurringEvents,
    exceedsQuota,
    plans,
    ticketPacks,
  ]);

  const handleSelectPlan = () => {
    if (recommendation.plan && onSelectPlan) {
      onSelectPlan(recommendation.plan.billing_type);
    }
    onHide();
  };

  // Check if current plan matches recommended plan
  const isCurrentPlan = recommendation.plan?.billing_type === subscription?.billing_type;

  // Get URL search params for instance (Wix compatibility)
  const urlSearchParams = new URLSearchParams(window.location.search);
  let instance = urlSearchParams.get('instance');
  if (!instance) {
    const hashFragment = window.location.hash.split('?')[1] || '';
    const hashParams = new URLSearchParams(hashFragment);
    instance = hashParams.get('instance');
  }

  // Group features by simplified category names
  const featuresByCategory = useMemo(() => {
    const categoryMapping: Record<string, string> = {
      'Free → Professional': 'Basic Event Features',
      'Professional → Business': 'Advanced Event Tools',
      'Business → Business+': 'Branding & Customization',
      'Business+ → Enterprise': 'Platform Features',
    };

    const categories: Record<
      string,
      Array<[string, (typeof FEATURE_TO_PLAN_MAP)[FeatureKey]]>
    > = {};
    Object.entries(FEATURE_TO_PLAN_MAP).forEach(([key, config]) => {
      const simplifiedCategory = categoryMapping[config.category] || config.category;
      if (!categories[simplifiedCategory]) {
        categories[simplifiedCategory] = [];
      }
      categories[simplifiedCategory].push([key, config]);
    });
    return categories;
  }, []);

  const categoryOrder = [
    'Basic Event Features',
    'Advanced Event Tools',
    'Branding & Customization',
    'Platform Features',
  ];

  return (
    <Dialog
      header="What Plan Do I Need?"
      visible={visible}
      onHide={onHide}
      modal
      draggable={false}
      className={styles.modal}
      style={{ width: '900px', maxWidth: '95vw' }}
    >
      {/* Live Recommendation Summary at Top */}
      {recommendation.plan && (
        <div className={styles.recommendationSummary}>
          <div className="flex justify-content-between align-items-center">
            <div>
              <span className="text-600 text-sm">
                {recommendation.packRequired ? 'Recommended Setup: ' : 'Recommended Plan: '}
              </span>
              <span className="font-bold text-900">{recommendation.plan.name}</span>
              {recommendation.packRequired && recommendation.ticketPack && (
                <span className="text-600 text-sm"> + Pack</span>
              )}
            </div>
            {!recommendation.isEnterprise && (
              <div className="font-bold" style={{ color: 'var(--brand-background-color)' }}>
                {recommendation.packRequired && recommendation.ticketPack
                  ? `$${recommendation.plan.monthly_price + recommendation.ticketPack.price}`
                  : `$${recommendation.plan.monthly_price}/month`}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Input Section - FIRST */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Tell us about your event</div>

        {/* Average Ticket Price Slider */}
        <div className={styles.inputGroup}>
          <div className="flex justify-content-between align-items-center mb-2">
            <label htmlFor="ticketPriceSlider" className="text-600 font-semibold">
              Average Ticket Price
            </label>
            <div className="font-bold text-xl" style={{ color: 'var(--brand-background-color)' }}>
              ${avgTicketPrice}
            </div>
          </div>
          <Slider
            id="ticketPriceSlider"
            value={avgTicketPrice}
            onChange={(e) => setAvgTicketPrice(e.value as number)}
            min={0}
            max={1000}
            step={5}
          />
          <div className="flex justify-content-between text-xs text-600 mt-1">
            <span>$0 (Free)</span>
            <span>$1,000</span>
          </div>
        </div>

        {/* Expected Attendees Slider */}
        <div className={styles.inputGroup}>
          <div className="flex justify-content-between align-items-center mb-2">
            <label htmlFor="attendeeSlider" className="text-600 font-semibold">
              Expected Attendees
            </label>
            <div className="font-bold text-xl" style={{ color: 'var(--brand-background-color)' }}>
              {expectedAttendees.toLocaleString()}
            </div>
          </div>
          <Slider
            id="attendeeSlider"
            value={expectedAttendees}
            onChange={(e) => setExpectedAttendees(e.value as number)}
            min={0}
            max={10000}
            step={50}
          />
          <div className="flex justify-content-between text-xs text-600 mt-1">
            <span>0</span>
            <span>10,000</span>
          </div>
        </div>

        {/* Estimated Revenue Display */}
        {estimatedRevenue > 0 && (
          <div className={styles.revenueDisplay}>
            <div className="text-600 text-sm">Estimated Event Revenue</div>
            <div className="font-bold text-2xl" style={{ color: 'var(--brand-background-color)' }}>
              ${estimatedRevenue.toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {/* Quick Options - Right after sliders */}
      <div className={styles.quickOptions}>
        <div className="font-semibold text-900 mb-3">Quick Options</div>

        <div className="flex align-items-center gap-2 mb-2">
          <Checkbox
            inputId="recurringEvents"
            checked={recurringEvents}
            onChange={(e) => setRecurringEvents(e.checked || false)}
          />
          <label htmlFor="recurringEvents" className="cursor-pointer flex-1">
            <div className="font-semibold text-900 text-sm">I run events every month</div>
            <div className="text-600 text-xs">
              Monthly quota resets - perfect for recurring events
            </div>
          </label>
        </div>

        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="exceedsQuota"
            checked={exceedsQuota}
            onChange={(e) => setExceedsQuota(e.checked || false)}
          />
          <label htmlFor="exceedsQuota" className="cursor-pointer flex-1">
            <div className="font-semibold text-900 text-sm">
              My event will exceed my monthly quota
            </div>
            <div className="text-600 text-xs">Show ticket pack options for additional capacity</div>
          </label>
        </div>
      </div>

      {/* Ticket Pack Options - Right after quick options */}
      {recommendation.packRequired && recommendation.plan && (
        <div className={styles.warningBox}>
          <div className="flex align-items-center gap-2 mb-2">
            <i className="pi pi-info-circle" style={{ color: '#f59e0b' }} />
            <span className="font-semibold text-900">Additional Ticket Capacity Needed</span>
          </div>
          <div className="text-700 text-sm mb-3">
            Your event needs {expectedAttendees.toLocaleString()} attendees.
            <br />
            Your plan quota: {recommendation.plan.monthly_attendee_quota.toLocaleString()} tickets
          </div>

          <div className="font-semibold text-900 mb-3">Ticket Packs</div>
          <div className={styles.packOptions}>
            {recommendation.allMatchingPacks.length > 0 ? (
              recommendation.allMatchingPacks.map((pack, index) => {
                const isRecommended = pack.id === recommendation.ticketPack?.id;
                return (
                  <div
                    key={pack.id || index}
                    className={`${styles.packOption} ${isRecommended ? styles.packOptionRecommended : ''}`}
                  >
                    {isRecommended && <div className={styles.recommendedBadge}>Recommended</div>}
                    <div className="font-bold text-900 text-base mb-2">{pack.name}</div>
                    <div className="text-600 text-sm mb-3">
                      {pack.ticket_quota.toLocaleString()} tickets
                    </div>
                    <div
                      className="font-bold text-2xl"
                      style={{ color: 'var(--brand-background-color)' }}
                    >
                      ${pack.price}
                    </div>
                  </div>
                );
              })
            ) : recommendation.ticketPack ? (
              <div className={`${styles.packOption} ${styles.packOptionRecommended}`}>
                <div className={styles.recommendedBadge}>Recommended</div>
                <div className="font-bold text-900 text-base mb-2">
                  {recommendation.ticketPack.name}
                </div>
                <div className="text-600 text-sm mb-3">
                  {recommendation.ticketPack.ticket_quota.toLocaleString()} tickets
                </div>
                <div
                  className="font-bold text-2xl"
                  style={{ color: 'var(--brand-background-color)' }}
                >
                  ${recommendation.ticketPack.price}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Features - Always Expanded */}
      <div className={styles.section}>
        <label className="text-600 mb-3 block font-semibold">Features</label>
        {categoryOrder.map((category) => {
          const features = featuresByCategory[category];
          if (!features) return null;

          return (
            <div key={category} className={styles.categoryGroup}>
              <div className={styles.categoryLabel}>
                <span className="font-semibold text-900">{category}</span>
              </div>

              <div className={styles.featureList}>
                {features.map(([featureKey, config]) => (
                  <div key={featureKey} className={styles.featureItem}>
                    <Checkbox
                      inputId={featureKey}
                      checked={selectedFeatures.has(featureKey as FeatureKey)}
                      onChange={() => toggleFeature(featureKey as FeatureKey)}
                    />
                    <label htmlFor={featureKey} className="ml-2 cursor-pointer flex-1">
                      <div className="font-semibold text-900 text-sm">{config.displayName}</div>
                      <div className="text-600 text-xs mt-1">{config.subText}</div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Divider */}
      <hr className={styles.divider} />

      {/* Recommendation Section */}
      {recommendation.plan && (
        <div className={styles.section}>
          <div className={styles.recommendationCard}>
            <div className="mb-3">
              <div className={styles.sectionTitle} style={{ marginBottom: '8px' }}>
                {recommendation.packRequired ? 'Recommended Setup' : 'Recommended Plan'}
              </div>
              {recommendation.reasonBullets.length > 0 && (
                <div className="text-600 text-sm">
                  Recommended for events with {recommendation.reasonBullets.join(', ')}.
                </div>
              )}
            </div>

            {/* Plan Details */}
            <div className={styles.setupBreakdown}>
              <div className="mb-3">
                <div className="text-600 text-xs font-semibold uppercase mb-1">
                  {recommendation.packRequired ? 'Subscription Plan' : 'Plan'}
                </div>
                <div className="flex justify-content-between align-items-center">
                  <div className="font-bold text-lg text-900">{recommendation.plan.name}</div>
                  {!recommendation.isEnterprise && (
                    <div
                      className="font-semibold"
                      style={{ color: 'var(--brand-background-color)' }}
                    >
                      ${recommendation.plan.monthly_price}/month
                    </div>
                  )}
                </div>
              </div>

              {/* Ticket Pack (if required) */}
              {recommendation.packRequired && recommendation.ticketPack && (
                <>
                  <div className="text-center mb-2">
                    <i className="pi pi-plus" style={{ color: '#9ca3af', fontSize: '14px' }} />
                  </div>
                  <div className="mb-3">
                    <div className="text-600 text-xs font-semibold uppercase mb-1">Ticket Pack</div>
                    <div className="flex justify-content-between align-items-center">
                      <div>
                        <div className="font-bold text-lg text-900">
                          {recommendation.ticketPack.name}
                        </div>
                        <div className="text-600 text-xs">
                          {recommendation.ticketPack.ticket_quota.toLocaleString()} tickets
                        </div>
                      </div>
                      <div
                        className="font-semibold"
                        style={{ color: 'var(--brand-background-color)' }}
                      >
                        ${recommendation.ticketPack.price}
                      </div>
                    </div>
                  </div>

                  {/* Total Cost */}
                  <div className="pt-3 border-top-1 border-300 mb-3">
                    <div className="flex justify-content-between align-items-center">
                      <div className="font-bold text-900">Total Cost</div>
                      <div
                        className="font-bold text-xl"
                        style={{ color: 'var(--brand-background-color)' }}
                      >
                        ${recommendation.plan.monthly_price + recommendation.ticketPack.price}
                      </div>
                    </div>
                    <div className="text-600 text-xs mt-1">
                      ${recommendation.plan.monthly_price}/month + $
                      {recommendation.ticketPack.price} one-time
                    </div>
                  </div>
                </>
              )}
            </div>

            {recommendation.requiredFeatures.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {recommendation.requiredFeatures.slice(0, 3).map((feature, index) => (
                  <span key={index} className={styles.featureTag}>
                    ✓ {feature}
                  </span>
                ))}
                {recommendation.requiredFeatures.length > 3 && (
                  <span className={styles.featureTag}>
                    +{recommendation.requiredFeatures.length - 3} more
                  </span>
                )}
              </div>
            )}

            {/* Subscription Plan Button */}
            {recommendation.isEnterprise ? (
              <Button
                onClick={() =>
                  window.open(
                    'mailto:support@ticketspot.com?subject=Enterprise Plan Inquiry',
                    '_blank',
                  )
                }
                priority="primary"
                size="large"
                className="w-full mb-3"
                style={{
                  backgroundColor: 'var(--brand-background-color)',
                  borderColor: 'var(--brand-background-color)',
                }}
              >
                Contact Sales
              </Button>
            ) : isCurrentPlan ? (
              <div
                className="w-full mb-3 p-3 text-center border-2 border-green-500 border-round"
                style={{ backgroundColor: 'rgba(34, 197, 94, 0.05)' }}
              >
                <i
                  className="pi pi-check-circle mr-2 text-green-600"
                  style={{ fontSize: '1.2rem' }}
                />
                <span className="font-bold text-900">Current Plan</span>
              </div>
            ) : (
              <form
                action={
                  siteData?.type === 'wix'
                    ? `${API_URL}/api/v1/dashboard/billing/wix/sessions?instance=${instance}&brand=wix`
                    : `${API_URL}/api/v1/dashboard/billing/sessions`
                }
                method="POST"
                className="w-full"
              >
                <input
                  type="hidden"
                  name="priceId"
                  value={
                    isYearly
                      ? recommendation.plan?.yearly_price_id
                      : recommendation.plan?.monthly_price_id
                  }
                />
                <input type="hidden" name="billingType" value={recommendation.plan?.billing_type} />
                <input type="hidden" name="isYearly" value={isYearly ? 'true' : 'false'} />
                {couponCode && <input type="hidden" name="couponName" value={couponCode} />}

                <Button
                  type="submit"
                  priority="primary"
                  size="large"
                  className="w-full mb-3"
                  style={{
                    backgroundColor: 'var(--brand-background-color)',
                    borderColor: 'var(--brand-background-color)',
                  }}
                >
                  Activate {recommendation.plan?.name} Plan
                </Button>
              </form>
            )}

            {/* Ticket Pack Button (if required) */}
            {recommendation.packRequired &&
              recommendation.ticketPack &&
              !recommendation.isEnterprise && (
                <form
                  action={`${API_URL}/api/v1/dashboard/billing/ticket-packs/checkout`}
                  method="POST"
                  className="w-full"
                >
                  <input type="hidden" name="packId" value={recommendation.ticketPack.pack_id} />

                  <Button
                    type="submit"
                    priority="secondary"
                    size="large"
                    className="w-full"
                    style={{
                      borderColor: 'var(--brand-background-color)',
                      color: 'var(--brand-background-color)',
                    }}
                  >
                    Activate {recommendation.ticketPack.name} Pack
                  </Button>
                </form>
              )}
          </div>

          {/* Cost Comparison with Revenue Context */}
          {!recommendation.isEnterprise && recommendation.eventbriteCost > 0 && (
            <div className={styles.costComparison}>
              {estimatedRevenue > 0 && (
                <div className="mb-3 pb-3 border-bottom-1 border-300">
                  <div className="text-600 text-xs mb-1">Estimated Revenue</div>
                  <div
                    className="font-bold text-2xl"
                    style={{ color: 'var(--brand-background-color)' }}
                  >
                    ${estimatedRevenue.toLocaleString()}
                  </div>
                  <div className="text-600 text-xs mt-1">
                    TicketSpot cost: ${recommendation.ticketSpotCost}
                    {recurringEvents ? '/month' : ''}
                  </div>
                </div>
              )}
              <div className="text-600 text-sm mb-2">
                <strong>TicketSpot:</strong> ${recommendation.ticketSpotCost}
                {recurringEvents ? '/month' : ''}
                <span className="mx-2">•</span>
                <strong>Eventbrite:</strong> ~$
                {Math.round(recommendation.eventbriteCost).toLocaleString()}
                {recurringEvents ? '/month' : ''}
              </div>
              <div className="text-xs font-semibold" style={{ color: '#059669' }}>
                You save ~$
                {Math.round(
                  recommendation.eventbriteCost - recommendation.ticketSpotCost,
                ).toLocaleString()}{' '}
                vs Eventbrite
              </div>
            </div>
          )}
        </div>
      )}

      {/* No plan found message */}
      {!recommendation.plan && (
        <div className={styles.section}>
          <div className="text-center p-4">
            <i className="pi pi-info-circle text-4xl mb-3 text-600" />
            <div className="text-xl font-semibold mb-2">Getting Started</div>
            <div className="text-600">
              {avgTicketPrice === 0 && selectedFeatures.size === 0
                ? 'Select features you need above to get a plan recommendation.'
                : 'Adjust your event details above to see our recommendation.'}
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};
